/* i18n-ignore-file — nội dung mẫu (tên khối, tin nhắn gửi khách, giá trị điều kiện) là DỮ LIỆU LƯU vào luồng, giữ tiếng Việt; chỉ tên/mô tả hiển thị lấy từ autoTemplates.json. */
/**
 * Thư viện mẫu luồng — chọn một mẫu là áp thẳng lên canvas (hoặc tạo luồng
 * mới từ mẫu), sửa nội dung rồi Lưu. Mẫu CHỈ dùng trigger/action mà backend
 * thật sự chạy (xem catalog.ts), đúng schema flowConfig của backend.
 */
import type { FlowConfig, FlowEdge, FlowNode } from '@/hooks/use-automation'
import { i18n } from '@/lib/i18n'
import { triggerMeta } from './catalog'
import { KHOANG_X } from './flow-adapter'

export type NhomMau = 'tiep_nhan' | 'hoi_thoai' | 'lich_hen' | 'cdp' | 'van_hanh'
export const NHOM_MAU: Record<NhomMau, { ten: string }> = {
  get tiep_nhan() { return { ten: i18n.t('autoTemplates:nhom.tiep_nhan') } },
  get hoi_thoai() { return { ten: i18n.t('autoTemplates:nhom.hoi_thoai') } },
  get lich_hen() { return { ten: i18n.t('autoTemplates:nhom.lich_hen') } },
  get cdp() { return { ten: i18n.t('autoTemplates:nhom.cdp') } },
  get van_hanh() { return { ten: i18n.t('autoTemplates:nhom.van_hanh') } },
}

/** Khớp đúng 4 nhóm ngành trên trang giới thiệu (`frontend/public/landing/index.html`, mục "Theo ngành") — chọn mẫu
 * đúng theo ngành đang kinh doanh, không phải phân loại kỹ thuật riêng của module Tự động hoá. */
export type Nganh = 'ban_le' | 'chuoi_cua_hang' | 'dich_vu' | 'sme'
export const NGANH: Record<Nganh, { ten: string }> = {
  get ban_le() { return { ten: i18n.t('autoTemplates:nganh.ban_le') } },
  get chuoi_cua_hang() { return { ten: i18n.t('autoTemplates:nganh.chuoi_cua_hang') } },
  get dich_vu() { return { ten: i18n.t('autoTemplates:nganh.dich_vu') } },
  get sme() { return { ten: i18n.t('autoTemplates:nganh.sme') } },
}

export interface MauWorkflow {
  id: string
  /** Tên hiển thị trong thư viện mẫu (theo ngôn ngữ giao diện). */
  readonly ten: string
  /** Tên luồng khi lưu: luôn tiếng Việt (dữ liệu lưu, không theo ngôn ngữ giao diện). */
  readonly tenLuu: string
  nhom: NhomMau
  /** Ngành phù hợp — để trống = dùng chung mọi ngành (vẫn hiện khi lọc theo ngành bất kỳ). */
  nganh?: Nganh[]
  readonly moTa: string
  /** Mô tả khi lưu vào luồng: luôn tiếng Việt. */
  readonly moTaLuu: string
  readonly khiNao: string
  readonly ketQua: string
  trigger: string
  flow: FlowConfig
}

// ── Dựng chuỗi khối trái → phải; `sai` là nhánh Sai của khối Điều kiện ──
type Buoc =
  | { kind: 'action'; label: string; actionType: string; config: Record<string, unknown> }
  | { kind: 'condition'; label: string; config: Record<string, unknown>; sai?: Buoc[] }
  | { kind: 'delay'; label: string; config: { duration: number; unit: string } }

function dung(trigger: string, triggerConfig: Record<string, unknown>, buoc: Buoc[]): FlowConfig {
  const nodes: FlowNode[] = []
  const edges: FlowEdge[] = []
  let truoc: { id: string; kind: string } = { id: 'trigger', kind: 'source' }
  buoc.forEach((b, i) => {
    const id = `n${i + 1}`
    nodes.push({
      id, type: b.kind, label: b.label, status: 'active', config: b.config,
      ...(b.kind === 'action' ? { actionType: b.actionType } : {}),
      position: { x: 40 + (i + 1) * KHOANG_X, y: 40 },
    })
    edges.push({ source: truoc.id, target: id, ...(truoc.kind === 'condition' ? { label: 'true' } : {}) })
    truoc = { id, kind: b.kind }
    if (b.kind === 'condition') b.sai?.forEach((s, j) => {
      const sid = `${id}s${j + 1}`
      nodes.push({
        id: sid, type: s.kind, label: s.label, status: 'active', config: s.config,
        ...(s.kind === 'action' ? { actionType: s.actionType } : {}),
        position: { x: 40 + (i + 2 + j) * KHOANG_X, y: 400 },
      })
      edges.push(j === 0 ? { source: id, target: sid, label: 'false' } : { source: `${id}s${j}`, target: sid })
    })
  })
  return {
    version: '2',
    trigger: { id: 'trigger', type: trigger, label: triggerMeta(trigger).ten, config: triggerConfig, position: { x: 40, y: 40 } },
    nodes, edges,
  }
}

const tin = (label: string, text: string): Buoc => ({ kind: 'action', label, actionType: 'send_message', config: { text } })
const nhan = (tag: string): Buoc => ({ kind: 'action', label: `Gắn nhãn "${tag}"`, actionType: 'add_tag', config: { tag } })
const giaiDoan = (label: string, lifecycleStage: string): Buoc => ({ kind: 'action', label, actionType: 'update_lifecycle', config: { lifecycleStage } })
const hen = (label: string, title: string, offsetHours: number): Buoc => ({ kind: 'action', label, actionType: 'create_appointment', config: { title, offsetHours } })
const cho = (label: string, duration: number, unit: 'minutes' | 'hours' | 'days'): Buoc => ({ kind: 'delay', label, config: { duration, unit } })
const bao = (label: string, recipients: 'assignee' | 'admins', title: string, message: string): Buoc =>
  ({ kind: 'action', label, actionType: 'send_notification', config: { recipients, title, message } })
const dieuKien = (label: string, rules: { field: string; op: string; value: string }[], logic: 'and' | 'or' = 'and', sai?: Buoc[]): Buoc =>
  ({ kind: 'condition', label, config: { logic, rules }, sai })

const MAU: Array<Omit<MauWorkflow, 'flow' | 'ten' | 'tenLuu' | 'moTa' | 'moTaLuu' | 'khiNao' | 'ketQua'> & { triggerConfig?: Record<string, unknown>; buoc: Buoc[] }> = [
  // ── Chào đón & tiếp nhận ─────────────────────────────────────────
  {
    id: 'mau_khach_moi', nhom: 'tiep_nhan', nganh: ['dich_vu', 'sme'], trigger: 'contact_created',
    buoc: [nhan('Khách mới'), hen('Hẹn gọi chào khách', 'Gọi chào khách mới, hỏi nhu cầu', 2)],
  },
  {
    id: 'mau_quan_tam_oa', nhom: 'tiep_nhan', nganh: ['sme'], trigger: 'user_follow_oa',
    buoc: [nhan('Quan tâm OA'), tin('Tin cảm ơn', 'Cảm ơn {{contact.firstName}} đã quan tâm {{org.name}}! Cần hỗ trợ gì bạn cứ nhắn tại đây nhé.')],
  },
  {
    id: 'mau_lead_web', nhom: 'tiep_nhan', nganh: ['sme'], trigger: 'web_form_submitted',
    buoc: [
      tin('Chào khách từ web', 'Chào bạn, {{org.name}} đã nhận được tin nhắn. Nhân viên sẽ phản hồi trong ít phút ạ.'),
      nhan('Lead website'),
      giaiDoan('Chuyển thành Lead', 'lead'),
    ],
  },
  {
    id: 'mau_chao_thanh_vien_moi', nhom: 'tiep_nhan', nganh: ['ban_le'], trigger: 'event_tracked',
    buoc: [
      dieuKien('Sự kiện là khách Evotech mới?', [{ field: 'trigger.eventName', op: 'eq', value: 'evotech_customer_created' }]),
      tin('Tin chào mừng', 'Chào {{contact.firstName}}! Cảm ơn bạn đã trở thành thành viên {{org.name}}. Bạn đang ở hạng {{contact.evotech_member_level}} với {{contact.evotech_points}} điểm — chúc bạn mua sắm vui vẻ!'),
      nhan('Thành viên Evotech'),
    ],
  },

  // ── Chăm sóc hội thoại ───────────────────────────────────────────
  {
    id: 'mau_hoi_gia', nhom: 'hoi_thoai', nganh: ['ban_le', 'sme'], trigger: 'message_received',
    buoc: [
      dieuKien('Tin nhắn hỏi giá?', [
        { field: 'message.text', op: 'contains', value: 'giá' },
        { field: 'message.text', op: 'contains', value: 'bao nhiêu' },
      ], 'or'),
      nhan('Hỏi giá'),
      giaiDoan('Chuyển thành Lead', 'lead'),
    ],
  },
  {
    id: 'mau_chua_tra_loi', nhom: 'hoi_thoai', nganh: ['chuoi_cua_hang', 'sme'], trigger: 'no_reply_24h',
    buoc: [
      nhan('Cần trả lời'),
      bao('Báo người phụ trách', 'assignee', '⏰ {{contact.fullName}} chờ trả lời {{trigger.noReplyHours}} giờ', 'Tin cuối: “{{trigger.lastMessageText}}”'),
      hen('Hẹn gọi lại', 'Gọi lại khách chưa được trả lời 24 giờ', 1),
    ],
  },
  {
    id: 'mau_im_lang', nhom: 'hoi_thoai', nganh: ['sme'], trigger: 'conversation_idle', triggerConfig: { threshold: '1h' },
    buoc: [
      dieuKien('Tin cuối là của nhân viên?', [{ field: 'trigger.lastSenderType', op: 'eq', value: 'staff' }]),
      tin('Tin hỏi thăm', '{{contact.firstName}} ơi, bạn còn cần {{org.name}} hỗ trợ gì thêm không ạ?'),
    ],
  },
  {
    id: 'mau_xin_danh_gia_don', nhom: 'hoi_thoai', nganh: ['ban_le'], trigger: 'order_delivered',
    buoc: [
      cho('Chờ 2 ngày', 2, 'days'),
      tin('Tin xin đánh giá', '{{contact.firstName}} ơi, sản phẩm dùng ổn không ạ? {{org.name}} rất mong nhận được đánh giá của bạn để phục vụ tốt hơn 🙏'),
    ],
  },

  // ── Lịch hẹn ─────────────────────────────────────────────────────
  {
    id: 'mau_nhac_lich_hen', nhom: 'lich_hen', nganh: ['dich_vu'], trigger: 'appointment_upcoming',
    buoc: [tin('Tin nhắc lịch', '{{contact.firstName}} ơi, {{org.name}} nhắc lịch hẹn "{{trigger.appointmentTitle}}" lúc {{trigger.appointmentTime}} ngày {{trigger.appointmentDate}} ạ.')],
  },
  {
    id: 'mau_sau_lich_hen', nhom: 'lich_hen', nganh: ['dich_vu'], trigger: 'appointment_completed',
    buoc: [cho('Chờ 1 ngày', 1, 'days'), tin('Tin xin nhận xét', '{{contact.firstName}} thấy buổi làm việc hôm qua với {{org.name}} thế nào ạ? Rất mong nhận góp ý của bạn.')],
  },
  {
    id: 'mau_nhac_dat_lich_tiep', nhom: 'lich_hen', nganh: ['dich_vu'], trigger: 'appointment_completed',
    buoc: [
      cho('Chờ 25 ngày', 25, 'days'),
      tin('Tin gợi ý đặt lịch tiếp theo', '{{contact.firstName}} ơi, đã khá lâu rồi bạn chưa quay lại {{org.name}}. Bạn có muốn đặt lịch chăm sóc tiếp theo không ạ?'),
    ],
  },

  // ── Khách thân thiết & CDP ───────────────────────────────────────
  {
    id: 'mau_sinh_nhat', nhom: 'cdp', nganh: ['ban_le', 'dich_vu'], trigger: 'birthday_detected',
    buoc: [tin('Tin chúc mừng', '{{org.name}} chúc {{contact.firstName}} sinh nhật thật vui và nhiều sức khoẻ! 🎂'), nhan('Sinh nhật')],
  },
  {
    id: 'mau_vip', nhom: 'cdp', nganh: ['ban_le', 'dich_vu'], trigger: 'tag_added',
    buoc: [
      dieuKien('Nhãn vừa gắn là VIP?', [{ field: 'trigger.tag', op: 'eq', value: 'VIP' }]),
      giaiDoan('Chuyển thành VIP', 'evangelist'),
      hen('Hẹn gọi tri ân', 'Gọi tri ân khách VIP', 24),
    ],
  },
  {
    id: 'mau_vip_tri_an', nhom: 'cdp', nganh: ['ban_le'], trigger: 'order_delivered',
    buoc: [
      dieuKien('Khách đang hạng Vàng?', [{ field: 'contact.evotech_member_level', op: 'eq', value: 'Vàng' }]),
      tin('Tin cảm ơn khách Vàng', 'Cảm ơn {{contact.firstName}} đã tin tưởng {{org.name}}! Bạn đang là khách hạng Vàng với {{contact.evotech_points}} điểm — luôn được ưu tiên hỗ trợ và nhận ưu đãi riêng nhé.'),
      nhan('Khách Vàng tri ân'),
    ],
  },

  // ── Vận hành ─────────────────────────────────────────────────────
  {
    id: 'mau_zns_loi', nhom: 'van_hanh', nganh: ['chuoi_cua_hang'], trigger: 'zns_failed',
    buoc: [
      nhan('ZNS lỗi'),
      bao('Báo quản trị', 'admins', 'ZNS gửi {{contact.fullName}} bị lỗi', 'Mẫu {{trigger.templateId}} — {{trigger.errorMessage}}'),
      hen('Hẹn liên hệ lại', 'Liên hệ khách qua kênh khác (ZNS lỗi)', 2),
    ],
  },
  {
    id: 'mau_nut_goi_lai', nhom: 'van_hanh', trigger: 'manual', triggerConfig: { buttonLabel: 'Nhắc gọi lại ngày mai' },
    buoc: [
      hen('Hẹn gọi lại', 'Gọi lại {{contact.fullName}} ({{contact.phone}}) — do {{trigger.userName}} đặt từ hồ sơ khách', 24),
      nhan('Cần gọi lại'),
    ],
  },
  {
    id: 'mau_webhook_lead', nhom: 'van_hanh', nganh: ['sme'], trigger: 'webhook',
    buoc: [
      dieuKien('Có số điện thoại?', [{ field: 'trigger.phone', op: 'exists', value: '' }]),
      nhan('Lead landing'),
      giaiDoan('Chuyển thành Lead', 'lead'),
      { kind: 'action', label: 'Ghi sự kiện CDP', actionType: 'track_event', config: { eventName: 'landing_submit', eventValue: '' } },
    ],
  },
  {
    id: 'mau_giao_theo_nhan_chi_nhanh', nhom: 'van_hanh', nganh: ['chuoi_cua_hang'], trigger: 'tag_added',
    buoc: [
      dieuKien('Nhãn vừa gắn là tên chi nhánh? (sửa lại giá trị cho đúng)', [{ field: 'trigger.tag', op: 'eq', value: 'Chi nhánh Hà Nội' }]),
      { kind: 'action', label: 'Giao cho nhân viên phụ trách', actionType: 'assign_agent', config: { userId: '' } },
    ],
  },
]

const viVi = (key: string) => i18n.t(`autoTemplates:mau.${key}`, { lng: 'vi' })

export const MAU_WORKFLOW: MauWorkflow[] = MAU.map(({ buoc, triggerConfig, ...m }) => ({
  ...m,
  get ten() { return i18n.t(`autoTemplates:mau.${m.id}.ten`) },
  get tenLuu() { return viVi(`${m.id}.ten`) },
  get moTa() { return i18n.t(`autoTemplates:mau.${m.id}.moTa`) },
  get moTaLuu() { return viVi(`${m.id}.moTa`) },
  get khiNao() { return i18n.t(`autoTemplates:mau.${m.id}.khiNao`) },
  get ketQua() { return i18n.t(`autoTemplates:mau.${m.id}.ketQua`) },
  flow: dung(m.trigger, triggerConfig ?? {}, buoc),
}))
